import FocusTrap from "focus-trap-react";
import { isFocusLost } from "PFCore/helpers/is_focus_lost";
import {
  MouseEvent as ReactMouseEvent,
  ReactNode,
  RefCallback,
  useCallback,
  useId,
  useRef,
  useState
} from "react";

import { FloatingDock } from "./components/floating_dock/floating_dock";
import type { DragHandleProps } from "./components/floating_dock/floating_dock_draggable";
import {
  FLOATING_EXPANDABLE_BUBBLE_DEFAULT_MIN_PANEL_SIZE,
  FLOATING_EXPANDABLE_BUBBLE_MARGIN,
  FLOATING_EXPANDABLE_BUBBLE_NAVBAR_WIDTH
} from "./constants";
import css from "./floating_expandable_bubble.module.scss";
import { useFloatingEntry, useFloatingPositionStoreActions } from "./floating_position_store";
import { useFloatingResize } from "./hooks/use_floating_resize";
import type { DropZone } from "./parts/get_active_drop_zone";
import { getMorphStyle } from "./parts/get_morph_style";
import { getResizeCursor, getResizeDirection } from "./parts/get_resize_direction";

export type { DragHandleProps, DropZone };

const RESIZE_EDGE_THRESHOLD = 8;

export type FloatingExpandableBubbleProps = {
  storageKey: string;
  bubbleSize: number;
  triggerZIndex: number;
  panelZIndex: number;
  panelSize: { width: number; height: number };
  minPanelSize?: { width: number; height: number };
  resizable?: boolean;
  dropZones?: DropZone[];
  onDragOverZone?: (zoneId: string | null) => void;
  onDropInZone?: (zoneId: string) => void;
  onDragStateChange?: (isDragging: boolean) => void;
  renderCollapsed: (props: {
    panelId: string;
    triggerRef: RefCallback<HTMLButtonElement>;
    onToggle: () => void;
  }) => ReactNode;
  renderExpanded: (props: {
    id: string;
    dragHandleProps: DragHandleProps;
    initialFocusRef: RefCallback<HTMLElement>;
    onClose: () => void;
    areHeaderControlsVisible: boolean;
  }) => ReactNode;
};

const DOCK_ID = "floating-expandable-bubble-dock";

export const FloatingExpandableBubble = ({
  storageKey,
  bubbleSize,
  triggerZIndex,
  panelZIndex,
  panelSize,
  minPanelSize = FLOATING_EXPANDABLE_BUBBLE_DEFAULT_MIN_PANEL_SIZE,
  resizable = false,
  dropZones,
  onDragOverZone,
  onDropInZone,
  onDragStateChange,
  renderCollapsed,
  renderExpanded
}: FloatingExpandableBubbleProps) => {
  const { position, isOpen } = useFloatingEntry(storageKey);
  const { setOpen } = useFloatingPositionStoreActions();
  const panelId = useId();
  const initialFocusNodeRef = useRef<HTMLElement | null>(null);
  const isFocusInsidePanelRef = useRef(false);
  const shouldFocusTriggerRef = useRef(false);
  const [isTrapPaused, setIsTrapPaused] = useState(false);
  const [resizeCursor, setResizeCursor] = useState<string>("default");
  const lastResizeCursorRef = useRef<string>("default");

  const initialFocusRef = useCallback<RefCallback<HTMLElement>>((node) => {
    initialFocusNodeRef.current = node;
  }, []);

  const triggerRef = useCallback<RefCallback<HTMLButtonElement>>((node) => {
    if (!node || !shouldFocusTriggerRef.current) {
      return;
    }
    shouldFocusTriggerRef.current = false;
    if (isFocusLost()) {
      node.focus();
    }
  }, []);

  const maxSize = {
    width:
      window.innerWidth - FLOATING_EXPANDABLE_BUBBLE_NAVBAR_WIDTH - 2 * FLOATING_EXPANDABLE_BUBBLE_MARGIN,
    height: window.innerHeight - 2 * FLOATING_EXPANDABLE_BUBBLE_MARGIN
  };

  const {
    size: resizeSize,
    isResizing,
    dragPosition,
    onResizeMouseDown
  } = useFloatingResize({
    storageKey,
    defaultSize: panelSize,
    minSize: minPanelSize,
    maxSize,
    enabled: resizable
  });

  const effectivePanelSize = resizable ? resizeSize : panelSize;

  const handleToggle = () => setOpen(storageKey, !isOpen);
  const handleClose = () => {
    shouldFocusTriggerRef.current = true;
    isFocusInsidePanelRef.current = false;
    setIsTrapPaused(false);
    setOpen(storageKey, false);
  };

  const getResizeDirectionFromEvent = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      if (!resizable) {
        return null;
      }
      const rect = event.currentTarget.getBoundingClientRect();
      return getResizeDirection(event.clientX, event.clientY, rect, RESIZE_EDGE_THRESHOLD);
    },
    [resizable]
  );

  const handlePanelMouseMove = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const direction = getResizeDirectionFromEvent(event);
      const cursor = direction !== null ? getResizeCursor(direction) : "default";
      if (cursor !== lastResizeCursorRef.current) {
        lastResizeCursorRef.current = cursor;
        setResizeCursor(cursor);
      }
    },
    [getResizeDirectionFromEvent]
  );

  const handlePanelMouseDown = useCallback(
    (event: ReactMouseEvent<HTMLDivElement>) => {
      const direction = getResizeDirectionFromEvent(event);
      if (direction !== null) {
        onResizeMouseDown(event, direction);
      }
    },
    [getResizeDirectionFromEvent, onResizeMouseDown]
  );

  const morphStyle = getMorphStyle({
    isOpen,
    bubbleSize,
    panelSize: effectivePanelSize,
    position,
    triggerZIndex,
    panelZIndex
  });

  return (
    <FloatingDock
      storageKey={storageKey}
      bubbleSize={bubbleSize}
      zIndex={isOpen ? panelZIndex : triggerZIndex}
      dockId={DOCK_ID}
      isOpen={isOpen}
      panelSize={effectivePanelSize}
      isResizing={isResizing}
      dragPosition={dragPosition}
      morphStyle={morphStyle}
      dropZones={dropZones}
      onDragOverZone={onDragOverZone}
      onDropInZone={onDropInZone}
      onDragStateChange={onDragStateChange}
      onPanelMouseMove={resizable ? handlePanelMouseMove : undefined}
      onPanelMouseDown={resizable ? handlePanelMouseDown : undefined}
      panelCursor={resizable ? resizeCursor : undefined}
      renderCollapsed={() => renderCollapsed({ panelId, triggerRef, onToggle: handleToggle })}
      renderExpanded={(dragHandleProps, areHeaderControlsVisible) => (
        <FocusTrap
          active={areHeaderControlsVisible}
          paused={isTrapPaused}
          focusTrapOptions={{
            initialFocus: () =>
              isFocusInsidePanelRef.current ? undefined : (initialFocusNodeRef.current ?? undefined),
            escapeDeactivates: () => {
              handleClose();
              return false;
            },
            allowOutsideClick: () => {
              setIsTrapPaused(true);
              return true;
            },
            returnFocusOnDeactivate: false,
            preventScroll: true
          }}
        >
          <div
            role="presentation"
            className={css.focusTrap}
            onFocus={() => {
              isFocusInsidePanelRef.current = true;
              setIsTrapPaused(false);
            }}
            onBlur={(event) => {
              isFocusInsidePanelRef.current = event.currentTarget.contains(event.relatedTarget);
            }}
          >
            {renderExpanded({
              id: panelId,
              dragHandleProps,
              initialFocusRef,
              onClose: handleClose,
              areHeaderControlsVisible
            })}
          </div>
        </FocusTrap>
      )}
    />
  );
};
